import { cloneElement, RefObject } from 'react';

import styled from 'styled-components';

import { EmptyState } from '@components/empty-state';
import { IconAiGradient } from '@components/icons/ai-gradient';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { useSelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { MessageItem } from './messages';

import { type ChatMessage, type LoadingLabel } from '../hooks';
import { LoadingStateDisplay } from '../loading-state-display';

const DEFAULT_INPUT_ROW_HEIGHT = 72;
const MESSAGE_GAP = pxToRem(12);

type MessagesContainerProps = {
  messages: ChatMessage[];
  loadingLabel: LoadingLabel | null;
  isBuilding: boolean;
  messagesRef: RefObject<HTMLDivElement>;
  messagesInnerRef: RefObject<HTMLDivElement>;
  handleScroll: () => void;
  inputRowHeight: number;
  pauseStickyForFrame: () => void;
  onSubmitAnswers: (answers: string[]) => Promise<void>;
  onAcceptPlan: () => Promise<void>;
  onRejectPlan: () => Promise<void>;
  isPlanLoading?: boolean;
};

export const MessagesContainer = ({
  messages,
  loadingLabel,
  isBuilding,
  messagesRef,
  messagesInnerRef,
  inputRowHeight,
  handleScroll,
  pauseStickyForFrame,
  onSubmitAnswers,
  onAcceptPlan,
  onRejectPlan,
  isPlanLoading,
}: MessagesContainerProps) => {
  const { t } = useScopedTranslate(locale);
  const { toggle: toggleDescription, isSelected: hasDescriptionOpen } = useSelectionStore();

  const bottomOffsetPx = messages.length === 0 ? DEFAULT_INPUT_ROW_HEIGHT : inputRowHeight;
  const bottomOffset = pxToRem(bottomOffsetPx + 24);

  const handleToggleDescription = (id: string) => {
    pauseStickyForFrame();
    toggleDescription(id);
  };

  return (
    <StyledMessagesContainer
      ref={messagesRef}
      onScroll={handleScroll}
      style={{
        bottom: bottomOffset,
        justifyContent: messages.length === 0 ? 'center' : 'flex-start',
      }}
    >
      <MessagesInner ref={messagesInnerRef}>
        {messages.length === 0 ? (
          <EmptyStateContainer>
            <EmptyState
              header={t('navigator')}
              content={t('description')}
              orientation="vertical"
              variant="small"
              transparent
              icon={<IconAiGradient />}
              iconShadow={cloneElement(<IconAiGradient />, { opacity: 0.1, size: 240 })}
            />
          </EmptyStateContainer>
        ) : (
          <>
            {messages.map((message, idx) => {
              const isPrevTool = idx > 0 && messages[idx - 1]?.role === 'tool';
              const isNextTool = idx < messages.length - 1 && messages[idx + 1]?.role === 'tool';

              return (
                <MessageItem
                  key={`row-${message.id}`}
                  message={message}
                  index={idx}
                  isPrevTool={isPrevTool}
                  isNextTool={isNextTool}
                  isDescriptionOpen={hasDescriptionOpen(message.id)}
                  onToggleDescription={() => {
                    return handleToggleDescription(message.id);
                  }}
                  onSubmitAnswers={onSubmitAnswers}
                  onAcceptPlan={onAcceptPlan}
                  onRejectPlan={onRejectPlan}
                  isPlanLoading={isPlanLoading}
                />
              );
            })}
            <LoadingStateDisplay loadingLabel={loadingLabel} isBuilding={isBuilding} />
          </>
        )}
      </MessagesInner>
    </StyledMessagesContainer>
  );
};

const StyledMessagesContainer = styled(Stack).attrs({
  space: 0,
  justify: 'flex-start',
})({
  position: 'absolute',
  left: pxToRem(16),
  right: pxToRem(16),
  top: pxToRem(16),

  overflowY: 'auto',
  scrollbarColor: `${theme.color.uiTrans10} transparent`,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: pxToRem(8),
    height: pxToRem(8),
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.color.uiTrans10,
    borderRadius: pxToRem(8),
  },
});

const MessagesInner = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: MESSAGE_GAP,
});

const EmptyStateContainer = styled.div({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const locale = {
  en: {
    navigator: 'Navigator',
    description: 'Talk to our AI Builder to build or edit your workflow',
  },
};
